import React, { lazy, Suspense } from 'react';
import { Navigate, Outlet, Route, useLocation } from 'react-router-dom';
// Imported from the module, not the `features/auth` barrel: the barrel re-exports
// a `.jsx` file, which jest's transform does not cover.
import { ProtectedRoute } from '../auth/components/ProtectedRoute';
import { RequireActiveAccount } from '../../router/guards';
import { featureFlags } from '../../config/featureFlags';
import { DASHBOARDS_PERMISSIONS } from './permissions';
import {
  DASHBOARDS_ROUTE_SEGMENT,
  DASHBOARD_ID_PARAM,
  EDITOR_SEGMENT,
  NEW_DASHBOARD_SEGMENT,
} from './constants';
import { DashboardDraftProvider } from './context/DashboardDraftContext';
import { DashboardErrorBoundary } from './components/DashboardErrorBoundary';
import { RouteSkeleton } from '../../Components/Skeleton/RouteSkeleton';

// One lazy chunk per page, pointing at the page module instead of the feature
// barrel: importing this file from AppRouter must not pull the whole feature into
// the main bundle.
//
// ⚠️ They are NOT left to the app's global <Suspense> — see the boundary inside
// `DashboardsGate` for why. A chunk that suspends above the draft provider takes
// the draft with it.
const DashboardCreatePage = lazy(() =>
  import('./pages/DashboardCreatePage/DashboardCreatePage').then((module) => ({
    default: module.DashboardCreatePage,
  })),
);
const DashboardEditorPage = lazy(() =>
  import('./pages/DashboardEditorPage/DashboardEditorPage').then((module) => ({
    default: module.DashboardEditorPage,
  })),
);
const SavedDashboardPage = lazy(() =>
  import('./pages/SavedDashboardPage/SavedDashboardPage').then((module) => ({
    default: module.SavedDashboardPage,
  })),
);

/** What the Home hands over when it opens the creation screen. */
interface DashboardsLocationState {
  /** Folder the Create menu was invoked from. `null`/absent = root. */
  folderId?: string | null;
}

/**
 * Everything the section shares: the flag, the `view` permission, an active
 * account, an error boundary and the draft.
 *
 * Gated behind REACT_APP_FEATURE_DASHBOARDS — a brand-new visible area, so
 * the PRD asks for a flag and a gradual rollout (PRD §8).
 *
 * The draft provider lives here and not in each page because the name typed on
 * the creation screen has to survive the hand-off to the canvas, and from
 * SL-1659 on the canvas reopens the sources editor and writes back into the same
 * draft.
 */
const DashboardsGate: React.FC = () => {
  const location = useLocation();
  const { folderId = null } = (location.state as DashboardsLocationState | null) ?? {};

  if (!featureFlags.dashboards) return <Navigate to=".." replace />;
  return (
    <ProtectedRoute requiredPermission={DASHBOARDS_PERMISSIONS.view}>
      <RequireActiveAccount>
        <DashboardErrorBoundary>
          <DashboardDraftProvider folderId={folderId}>
            {/* The boundary belongs HERE, below the provider, and not to the
                app's global one above every route.
                
                Each page of this section is its own lazy chunk, so moving
                between them suspends. A boundary above the provider replaces
                the whole subtree with its fallback while the chunk loads — the
                provider unmounts, and the draft the user has been filling in
                goes with it. Going from the form to the canvas lost the folder
                the dashboard was being created in, and would have lost the name
                and the main table too if the canvas did not bounce an empty
                draft back to the form.
                
                Below the provider, only the page suspends. */}
            <Suspense fallback={<RouteSkeleton />}>
              <Outlet />
            </Suspense>
          </DashboardDraftProvider>
        </DashboardErrorBoundary>
      </RequireActiveAccount>
    </ProtectedRoute>
  );
};

/**
 * Route subtree of the Dashboards section, mounted by AppRouter under an account
 * (`accounts/:accountId`) — not under an agent: a dashboard is a first-level
 * object of the account, like a folder or a table (PRD §1).
 *
 * A `Route` element and not a component: `<Routes>` only accepts `Route` children,
 * and a wrapper component would break routing silently.
 *
 * The parent route carries what every screen shares and each leaf adds only the
 * permission that differs. Nesting `ProtectedRoute` means both must pass, so
 * someone who could create but not view the section stays out, which is the intent.
 *
 * The section has four surfaces (see the table in `paths.ts`). The two that need
 * a saved dashboard — `:dashboardId` for the read-only view and
 * `:dashboardId/editor` for editing it — are one component in two modes
 * (`SavedDashboardPage`), and they come **after** `new` so an id can never
 * shadow that segment.
 *
 * The read-only view carries only `view`; editing adds `edit` on top. Nesting
 * `ProtectedRoute` means both must pass, which is exactly the PRD's split between
 * Ver and Editar.
 *
 * Why the mode is in the path and not in component state, which is what the
 * prototype does: Back has to leave the editor, a read-only link has to be
 * shareable, and `Ver` and `Editar` are separate role permissions (PRD §4), so
 * the gate belongs on the route.
 *
 * There is no `index` route: a listing of dashboards does not exist — they are
 * found as cards in the Home, in their folder (SL-1669-B, which waits on the
 * backend carrying a dashboard's `folderId` and listing them per folder).
 */
export const dashboardsRoutes = (
  <Route path={DASHBOARDS_ROUTE_SEGMENT} element={<DashboardsGate />}>
    <Route
      path={NEW_DASHBOARD_SEGMENT}
      element={
        <ProtectedRoute requiredPermission={DASHBOARDS_PERMISSIONS.create}>
          <DashboardCreatePage />
        </ProtectedRoute>
      }
    />

    <Route
      path={`${NEW_DASHBOARD_SEGMENT}/${EDITOR_SEGMENT}`}
      element={
        <ProtectedRoute requiredPermission={DASHBOARDS_PERMISSIONS.create}>
          <DashboardEditorPage />
        </ProtectedRoute>
      }
    />

    <Route path={`:${DASHBOARD_ID_PARAM}`} element={<SavedDashboardPage surface="view" />} />

    <Route
      path={`:${DASHBOARD_ID_PARAM}/${EDITOR_SEGMENT}`}
      element={
        <ProtectedRoute requiredPermission={DASHBOARDS_PERMISSIONS.edit}>
          <SavedDashboardPage surface="editor" />
        </ProtectedRoute>
      }
    />
  </Route>
);
